import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, ShoppingCart, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

/**
 * ProductAccessories - Shows recommended accessories on product detail page.
 */
export function ProductAccessories({ productId }) {
    const [accessories, setAccessories] = useState([]);
    const { addItem } = useCart();

    useEffect(() => {
        if (!productId) return;
        api.get(`/accessories/product/${productId}?limit=6`)
            .then(r => setAccessories(r.data || []))
            .catch(() => {});
    }, [productId]);

    if (accessories.length === 0) return null;

    return (
        <div className="mt-8" data-testid="product-accessories">
            <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-amber-500" />
                <h3 className="text-lg font-bold text-gray-900">Doporucene prislusenstvi</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {accessories.map(p => (
                    <div key={p.id} className="bg-white rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all overflow-hidden group"
                        data-testid={`accessory-${p.slug}`}>
                        <Link to={`/produkt/${p.slug}`}>
                            <div className="aspect-square bg-gray-50 flex items-center justify-center p-2 overflow-hidden">
                                {p.main_image ? (
                                    <img src={p.main_image} alt={p.name}
                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                                ) : (
                                    <Package size={20} className="text-gray-300" />
                                )}
                            </div>
                        </Link>
                        <div className="p-2.5">
                            <Link to={`/produkt/${p.slug}`}>
                                <p className="text-[11px] font-medium text-gray-700 group-hover:text-green-700 leading-tight line-clamp-2 transition-colors mb-1">
                                    {p.name}
                                </p>
                            </Link>
                            <div className="flex items-center justify-between gap-1">
                                <p className="font-bold text-sm" style={{ color: "var(--sg-primary)" }}>
                                    {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kc
                                </p>
                                <button
                                    onClick={() => {
                                        addItem({
                                            product_id: p.id,
                                            name: p.name,
                                            price: p.price,
                                            sale_price: p.sale_price,
                                            quantity: 1,
                                            main_image: p.main_image,
                                        });
                                        toast.success("Pridano do kosiku");
                                    }}
                                    className="w-6 h-6 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center flex-shrink-0 transition-colors"
                                    data-testid={`add-accessory-${p.slug}`}
                                    title="Pridat do kosiku">
                                    <Plus size={12} className="text-green-700" />
                                </button>
                            </div>
                            {p.accessory_note && (
                                <p className="text-[9px] text-gray-400 mt-1 leading-tight">{p.accessory_note}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


/**
 * CartAccessories - Shows recommended accessories in the cart sidebar.
 */
export function CartAccessories({ cartItems }) {
    const [accessories, setAccessories] = useState([]);
    const { addItem } = useCart();

    useEffect(() => {
        if (!cartItems || cartItems.length === 0) return;
        const ids = cartItems.map(i => i.product_id).filter(Boolean).join(",");
        if (!ids) return;
        api.get(`/accessories/cart?product_ids=${ids}&limit=3`)
            .then(r => setAccessories(r.data || []))
            .catch(() => {});
    }, [cartItems]);

    if (accessories.length === 0) return null;

    return (
        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="cart-accessories">
            <div className="flex items-center gap-1.5 mb-3">
                <Sparkles size={12} className="text-amber-500" />
                <p className="text-xs font-bold text-gray-700">Doporucujeme dokoupit</p>
            </div>
            <div className="space-y-2">
                {accessories.map(p => (
                    <div key={p.id} className="flex items-center gap-2 py-1.5" data-testid={`cart-acc-${p.slug}`}>
                        <Link to={`/produkt/${p.slug}`} className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
                                {p.main_image ? (
                                    <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-0.5" loading="lazy" />
                                ) : (
                                    <Package size={12} className="text-gray-300" />
                                )}
                            </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                            <Link to={`/produkt/${p.slug}`}>
                                <p className="text-[10px] text-gray-600 hover:text-green-700 leading-tight line-clamp-1 transition-colors">{p.name}</p>
                            </Link>
                            <p className="text-xs font-bold" style={{ color: "var(--sg-primary)" }}>
                                {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kc
                            </p>
                        </div>
                        <button
                            onClick={() => {
                                addItem({
                                    product_id: p.id,
                                    name: p.name,
                                    price: p.price,
                                    sale_price: p.sale_price,
                                    quantity: 1,
                                    main_image: p.main_image,
                                });
                                toast.success("Pridano do kosiku");
                            }}
                            className="w-7 h-7 rounded-lg bg-green-50 hover:bg-green-100 flex items-center justify-center flex-shrink-0 transition-colors border border-green-200"
                            data-testid={`add-cart-acc-${p.slug}`}
                            title="Pridat do kosiku">
                            <ShoppingCart size={11} className="text-green-700" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
